import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Menu, X, Moon, Sun, LogOut, User } from 'lucide-react';
import { toggleDarkMode } from '../../redux/slices/themeSlice';
import { logout } from '../../redux/slices/authSlice';
import toast from 'react-hot-toast';

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { darkMode } = useSelector((state) => state.theme);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = () => {
    dispatch(logout());
    toast.success('Logged out successfully');
    navigate('/');
  };

  const toggleTheme = () => {
    dispatch(toggleDarkMode());
  };

  return (
    <nav className="bg-white dark:bg-gray-800 shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg">S</span>
            </div>
            <span className="text-xl font-bold text-gray-800 dark:text-white hidden sm:inline">
              Elite Salon
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <NavLink
              to="/"
              className={({ isActive }) => `transition pb-1 ${isActive ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 dark:text-gray-300 hover:text-blue-600'}`}
            >
              Home
            </NavLink>
            <NavLink
              to="/services"
              className={({ isActive }) => `transition pb-1 ${isActive ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 dark:text-gray-300 hover:text-blue-600'}`}
            >
              Services
            </NavLink>
            <NavLink
              to="/contact"
              className={({ isActive }) => `transition pb-1 ${isActive ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 dark:text-gray-300 hover:text-blue-600'}`}
            >
              Contact
            </NavLink>
            {isAuthenticated && (
              <NavLink
                to="/appointments"
                className={({ isActive }) => `transition pb-1 ${isActive ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 dark:text-gray-300 hover:text-blue-600'}`}
              >
                Appointments
              </NavLink>
            )}
          </div>

          {/* Right Side Icons & Buttons */}
          <div className="flex items-center space-x-4">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition"
              aria-label="Toggle theme"
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {/* Auth Buttons */}
            {!isAuthenticated ? (
              <div className="hidden sm:flex space-x-2">
                <Link
                  to="/login"
                  className="px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900 transition"
                >
                  Login
                </Link>
                <Link
                  to="/admin-login"
                  className="px-4 py-2 text-orange-600 border border-orange-600 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900 transition font-semibold"
                  title="Admin Portal"
                >
                  🔐 Admin
                </Link>
                <Link
                  to="/signup"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  Sign Up
                </Link>
              </div>
            ) : (
              <div className="hidden sm:flex items-center space-x-4">
                {user?.role === 'admin' && (
                  <Link
                    to="/admin/dashboard"
                    className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm"
                  >
                    Admin
                  </Link>
                )}
                {user?.role === 'barber' && (
                  <Link
                    to="/barber/dashboard"
                    className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm"
                  >
                    Dashboard
                  </Link>
                )}
                <Link
                  to="/profile"
                  className="flex items-center space-x-1 text-gray-600 dark:text-gray-300 hover:text-blue-600 transition"
                >
                  <User size={20} />
                  <span className="hidden lg:inline text-sm">{user?.firstName}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-1 text-red-600 hover:text-red-700 transition"
                >
                  <LogOut size={20} />
                </button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 rounded-lg"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden pb-4 space-y-2">
            <NavLink
              to="/"
              className={({ isActive }) => `block px-4 py-2 rounded ${isActive ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            >
              Home
            </NavLink>
            <NavLink
              to="/services"
              className={({ isActive }) => `block px-4 py-2 rounded ${isActive ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            >
              Services
            </NavLink>
            <NavLink
              to="/contact"
              className={({ isActive }) => `block px-4 py-2 rounded ${isActive ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            >
              Contact
            </NavLink>
            {isAuthenticated && (
              <>
                <Link
                  to="/appointments"
                  className="block px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                >
                  Appointments
                </Link>
                <Link
                  to="/profile"
                  className="block px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                >
                  Profile
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded"
                >
                  Logout
                </button>
              </>
            )}
            {!isAuthenticated && (
              <div className="space-y-2">
                <Link
                  to="/login"
                  className="block px-4 py-2 text-blue-600 border border-blue-600 rounded hover:bg-blue-50 dark:hover:bg-blue-900"
                >
                  Login
                </Link>
                <Link
                  to="/admin-login"
                  className="block px-4 py-2 text-orange-600 border border-orange-600 rounded hover:bg-orange-50 dark:hover:bg-orange-900 font-semibold"
                >
                  🔐 Admin Login
                </Link>
                <Link
                  to="/signup"
                  className="block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
